import React, { useEffect, useState } from 'react';
import {
  getStudentProfileDetail,
  getSsaDashboardPassword,
  resetSsaDashboardPassword,
} from './service/ssaProfileApi';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import {
  CardHeader,
  FieldValue,
  ProfileCard,
  HeaderTitle,
  SsaProfileButton,
  HeaderIconWrapper,
  PasswordTypography,
  PasswordDisplayBox,
  ActionButtonsContainer,
  StyledAccordionSummary,
  StyledAccordionDetails,
} from './service/SsaProfileTabStyled';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { DataField } from './SsaProfileTabUtils';
import PersonIcon from '@mui/icons-material/Person';
import PublicIcon from '@mui/icons-material/Public';
import Visibility from '@mui/icons-material/Visibility';
import { AccordianContainer } from 'components/Ui/styled';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import DescriptionIcon from '@mui/icons-material/Description';
import { SsaStudentProfileLoader } from '../../ssaCommonUtils';
import { useNotification, handleBooleanProp } from 'utils/commonUtils';
import ConfirmationDialog from 'components/Common/ConfirmationDialog/ConfirmationDialog';
import { usePermissionCustomHook } from 'layouts/users/userPermissions/utils/permissionsUtils';
import StudentProfileNote from 'layouts/studentManagement/components/studentProfile/components/studentProfileTabs/StudentProfileNote';

const SsaProfileTab = () => {
  const canViewStudentPassword = usePermissionCustomHook(
    'CAN_VIEW_STUDENT_DASHBOARD_PASSWORDS',
  );
  const canResetStudentPassword = usePermissionCustomHook(
    'CAN_RESET_STUDENT_DASHBOARD_PASSWORDS',
  );
  const { NotificationPopup, handleErrorResponse } = useNotification();

  const ssaStudentDetail = useSelector(
    (state) => state?.ssaStudentDetail?.selectedStudentHeadingDetails || {},
  );
  const { studentId } = ssaStudentDetail || {};

  const [password, setPassword] = useState({
    dashboardPassword: '',
    error: false,
  });
  const [isExpanded, setIsExpanded] = useState(true);
  const [profileDetails, setProfileDetails] = useState([]);
  const [resetLoadingButton, setResetLoadingButton] = useState(false);
  const [passwordBoxVisible, setPasswordBoxVisible] = useState(false);
  const [profileDetailsLoading, setProfileDetailsLoading] = useState(false);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);

  const {
    note,
    tags,
    gender,
    stateId,
    lastName,
    schoolId,
    firstName,
    resourceId,
    iepTeacher,
    gradeLevel,
    studentType,
    dateOfBirth,
    raceEthnicity,
    homePrimaryLanguage,
  } = profileDetails?.profile || {};

  const useFunction = useForm({
    mode: 'onChange',
  });

  const { handleSubmit } = useFunction;

  useEffect(() => {
    fetchStudentProfileDetails();
    if (canViewStudentPassword) {
      fetchDashbardPassword();
    }
  }, []);

  const fetchStudentProfileDetails = async () => {
    try {
      setProfileDetailsLoading(true);
      const getProfileDetail = await getStudentProfileDetail(studentId);
      setProfileDetails(getProfileDetail);
      setProfileDetailsLoading(false);
    } catch (error) {
      setProfileDetailsLoading(false);
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  const fetchStudentNote = async () => {
    try {
      const getProfileDetail = await getStudentProfileDetail(studentId);
      setProfileDetails(getProfileDetail);
    } catch (error) {
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  if (profileDetailsLoading) {
    return <SsaStudentProfileLoader />;
  }

  const fetchDashbardPassword = async () => {
    try {
      const getPassword = await getSsaDashboardPassword(studentId);

      setPassword({
        dashboardPassword: getPassword?.dashboardPassword,
        error: false,
      });
    } catch (error) {
      const errorMsg =
        error?.response?.data?.message || 'Failed to load password';
      handleErrorResponse(errorMsg);

      setPassword({
        dashboardPassword: errorMsg,
        error: true,
      });
    }
  };

  const onSubmit = async () => {
    try {
      setResetLoadingButton(true);
      await resetSsaDashboardPassword(studentId);
      await fetchDashbardPassword();
      setResetLoadingButton(false);
      setOpenConfirmationDialog(false);
    } catch (error) {
      setResetLoadingButton(false);
      setOpenConfirmationDialog(false);
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  const handleResetPassword = () => {
    setOpenConfirmationDialog(true);
  };

  const passwordText = passwordBoxVisible ? 'Hide Password' : 'View Password';
  const passwordIcon = passwordBoxVisible ? <VisibilityOff /> : <Visibility />;

  const handleAccordionChange = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <>
      <Grid container spacing={2}>
        {/* Basic Information Card */}
        <Grid item xs={12} md={5}>
          <ProfileCard>
            <CardHeader>
              <HeaderIconWrapper bgColor="#edf2ff">
                <PersonIcon fontSize="small" />
              </HeaderIconWrapper>
              <HeaderTitle>Basic Information</HeaderTitle>
            </CardHeader>
            <Box>
              <DataField label="Full Name" value={`${firstName} ${lastName}`} />
              <DataField label="Student ID" value={resourceId} isPill />
              <DataField label="Grade Level" value={gradeLevel} />
              <DataField label="Student Type" value={studentType} />
              <DataField label="School" value={schoolId} />
              <DataField label="State ID" value={stateId} />
              <DataField
                label="Tags"
                value={tags?.join(', ') || 'None assigned'}
              />
              <DataField
                label="IEP Teacher"
                value={iepTeacher || 'Not assigned'}
              />
            </Box>
          </ProfileCard>
        </Grid>

        {/* Demographics Card */}
        <Grid item xs={12} md={7}>
          <ProfileCard>
            <CardHeader>
              <HeaderIconWrapper bgColor="#e6fffa">
                <PublicIcon fontSize="small" />
              </HeaderIconWrapper>
              <HeaderTitle>Demographics</HeaderTitle>
            </CardHeader>
            <Box>
              <DataField label="Gender" value={gender} />
              <DataField label="Date of Birth" value={dateOfBirth} />
              <DataField
                label="Age"
                value={
                  dateOfBirth
                    ? `${new Date().getFullYear() - new Date(dateOfBirth).getFullYear()} years old`
                    : '--'
                }
              />
              <DataField label="Race / Ethnicity" value={raceEthnicity} />
              <DataField label="Home Language" value={homePrimaryLanguage} />
            </Box>
          </ProfileCard>
        </Grid>

        {/* Dashboard Password Section */}
        {canViewStudentPassword && (
          <Grid item xs={12}>
            <ProfileCard>
              <CardHeader>
                <HeaderIconWrapper bgColor="#fff5f5">
                  <RestartAltIcon fontSize="small" />
                </HeaderIconWrapper>
                <HeaderTitle>Dashboard Password</HeaderTitle>
              </CardHeader>

              <ActionButtonsContainer>
                <SsaProfileButton
                  variant="outlined"
                  startIcon={passwordIcon}
                  onClick={() => setPasswordBoxVisible(!passwordBoxVisible)}
                >
                  {passwordText}
                </SsaProfileButton>

                {/* Reset Password Button */}
                {canResetStudentPassword && (
                  <SsaProfileButton
                    variant="outlined"
                    startIcon={<RestartAltIcon />}
                    onClick={handleResetPassword}
                  >
                    Reset Password
                  </SsaProfileButton>
                )}
              </ActionButtonsContainer>

              {/* Password Display */}
              {passwordBoxVisible && (
                <PasswordDisplayBox>
                  <FieldValue>🔑 Dashboard Password:</FieldValue>
                  <PasswordTypography
                    variant="body2"
                    fontWeight="bold"
                    error={handleBooleanProp(password?.error)}
                  >
                    {password?.dashboardPassword}
                  </PasswordTypography>
                </PasswordDisplayBox>
              )}
            </ProfileCard>
          </Grid>
        )}

        <Grid item xs={12}>
          <ProfileCard>
            <AccordianContainer
              expanded={isExpanded}
              defaultExpanded={false}
              onChange={handleAccordionChange}
              sx={{ border: 0 }}
            >
              <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
                <CardHeader>
                  <HeaderIconWrapper bgColor="#fffaf0">
                    <DescriptionIcon fontSize="small" />
                  </HeaderIconWrapper>
                  <HeaderTitle>Notes</HeaderTitle>
                </CardHeader>
              </StyledAccordionSummary>
              <StyledAccordionDetails>
                <StudentProfileNote
                  note={note}
                  resourceId={resourceId}
                  fetchStudentNote={fetchStudentNote}
                />
              </StyledAccordionDetails>
            </AccordianContainer>
          </ProfileCard>
        </Grid>
      </Grid>
      <ConfirmationDialog
        isSubmit={true}
        onSubmit={onSubmit}
        handleSubmit={handleSubmit}
        loadingButton={resetLoadingButton}
        openConfirmationDialog={openConfirmationDialog}
        setOpenConfirmationDialog={setOpenConfirmationDialog}
        title="Are you sure you want to reset dashboard password ?"
      />
      <NotificationPopup />
    </>
  );
};

export default SsaProfileTab;
